import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, MapPin } from 'lucide-react';
import { PageLayout, FormLayout, FormField } from '../common/index.js';

const EditAttendance = () => {
  const { attendanceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [formData, setFormData] = useState({
    attendance_date: '',
    login_time: '',
    logout_time: '',
    login_village: '',
    login_city: '',
    login_district: '',
    login_state: '',
    login_country: '',
    logout_village: '',
    logout_city: '',
    logout_district: '',
    logout_state: '',
    logout_country: ''
  });

  useEffect(() => {
    fetchAttendance();
  }, [attendanceId]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/attendance/${attendanceId}`);
      const data = response.data.data;
      setAttendance(data);
      
      // Format data for form
      setFormData({
        attendance_date: data.attendance_date ? data.attendance_date.split('T')[0] : '',
        login_time: data.login_time ? formatTimeForInput(data.login_time) : '',
        logout_time: data.logout_time ? formatTimeForInput(data.logout_time) : '',
        login_village: data.login_location?.village || '',
        login_city: data.login_location?.city || '',
        login_district: data.login_location?.district || '',
        login_state: data.login_location?.state || '',
        login_country: data.login_location?.country || '',
        logout_village: data.logout_location?.village || '',
        logout_city: data.logout_location?.city || '',
        logout_district: data.logout_location?.district || '',
        logout_state: data.logout_location?.state || '',
        logout_country: data.logout_location?.country || ''
      });
    } catch (err) {
      Alert.error('Failed to load attendance details');
      console.error(err);
      navigate('/attendance');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);

      // Combine date and time
      const loginDateTime = formData.login_time 
        ? `${formData.attendance_date}T${formData.login_time}:00`
        : null;
      
      const logoutDateTime = formData.logout_time 
        ? `${formData.attendance_date}T${formData.logout_time}:00`
        : null;

      const updateData = {
        attendance_date: formData.attendance_date,
        login_time: loginDateTime,
        logout_time: logoutDateTime,
        login_village: formData.login_village,
        login_city: formData.login_city,
        login_district: formData.login_district,
        login_state: formData.login_state,
        login_country: formData.login_country,
        logout_village: formData.logout_village || null,
        logout_city: formData.logout_city || null,
        logout_district: formData.logout_district || null,
        logout_state: formData.logout_state || null,
        logout_country: formData.logout_country || null
      };

      await axiosInstance.put(`/attendance/${attendanceId}`, updateData);
      Alert.success('Attendance updated successfully');
      navigate('/attendance');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update attendance');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Edit Attendance" icon={Calendar}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading attendance details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!attendance) {
    return null;
  }

  return (
    <FormLayout
      title="Edit Attendance"
      icon={Calendar}
      onBack={() => navigate('/attendance')}
      onSubmit={handleSubmit}
      submitLabel={saving ? 'Saving...' : 'Save Changes'}
      onCancel={() => navigate('/attendance')}
      loading={saving}
      maxWidth="max-w-4xl"
    >
      <p className="text-sm text-gray-600 -mt-2 mb-2">Update attendance record for {attendance.user_name}</p>

      <div>
        <h3 className="text-base font-semibold text-secondary mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Basic Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormField label="Date" required>
            <input type="date" name="attendance_date" value={formData.attendance_date} onChange={handleInputChange} required className="input-clean w-full" />
          </FormField>
          <FormField label="Login Time" required>
            <input type="time" name="login_time" value={formData.login_time} onChange={handleInputChange} required className="input-clean w-full" />
          </FormField>
          <FormField label="Logout Time">
            <input type="time" name="logout_time" value={formData.logout_time} onChange={handleInputChange} className="input-clean w-full" />
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-secondary mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand" /> Login Location
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {['login_village', 'login_city', 'login_district', 'login_state'].map((field) => (
            <FormField key={field} label={field.replace('login_', '').replace(/^\w/, (c) => c.toUpperCase())}>
              <input type="text" name={field} value={formData[field]} onChange={handleInputChange} className="input-clean w-full" />
            </FormField>
          ))}
          <FormField label="Country" className="sm:col-span-2">
            <input type="text" name="login_country" value={formData.login_country} onChange={handleInputChange} className="input-clean w-full" />
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-secondary mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-green-500" /> Logout Location
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {['logout_village', 'logout_city', 'logout_district', 'logout_state'].map((field) => (
            <FormField key={field} label={field.replace('logout_', '').replace(/^\w/, (c) => c.toUpperCase())}>
              <input type="text" name={field} value={formData[field]} onChange={handleInputChange} className="input-clean w-full" />
            </FormField>
          ))}
          <FormField label="Country" className="sm:col-span-2">
            <input type="text" name="logout_country" value={formData.logout_country} onChange={handleInputChange} className="input-clean w-full" />
          </FormField>
        </div>
      </div>
    </FormLayout>
  );
};

export default EditAttendance;